/**
 * Landing page for the games hub. Lists every registered game with a
 * "Create new room" button that routes to `/create/:gameId`.
 */

import { Box, Button, Paper, Stack, Text, Title } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { GAMES } from '../registry'
import type { GameId } from '@mdev/common'
import type { GameModule } from '../module'

export function HubHomePage() {
  // Filter out empty registry slots — `Partial<Record<GameId, GameModule>>`.
  const entries = Object.entries( GAMES ).filter(
    ( entry ): entry is [ GameId, GameModule<unknown, unknown> ] => Boolean( entry[ 1 ] ),
  )

  return (
    <Box
      style={ { minHeight: '100vh', background: '#0B0F1A', color: 'white' } }
      p="xl"
    >
      <Stack
        gap="xl"
        maw={ 720 }
        mx="auto"
      >
        <Stack gap={ 4 }>
          <Title
            order={ 1 }
            c="white"
          >
            Games
          </Title>
          <Text c="dimmed">Pick a game to host. Players join from their phones.</Text>
        </Stack>

        { entries.length === 0 ? (
          <Paper
            p="lg"
            withBorder
            style={ { borderColor: '#1F2937', background: '#0F172A' } }
          >
            <Text c="dimmed">No games registered yet.</Text>
          </Paper>
        ) : (
          <Stack gap="md">
            { entries.map( ( [ id, mod ] ) => (
              <Paper
                key={ id }
                p="lg"
                withBorder
                style={ { borderColor: '#1F2937', background: '#0F172A' } }
              >
                <Stack gap="sm">
                  <Title
                    order={ 3 }
                    c="white"
                  >
                    { mod.displayName }
                  </Title>
                  <Text
                    size="sm"
                    c="dimmed"
                  >
                    { mod.minPlayers === mod.maxPlayers
                      ? `${ mod.minPlayers } players`
                      : `${ mod.minPlayers }–${ mod.maxPlayers } players` }
                  </Text>
                  <Button
                    component={ Link }
                    to={ `/games/create/${ id }` }
                    // Launch the live session in its own tab so the hub (with
                    // nav) stays put. The session is full-screen and meant to
                    // run on the host's display.
                    target="_blank"
                    rel="noopener"
                    color="yellow"
                    rightSection={ <IconArrowRight size={ 16 } /> }
                    style={ { alignSelf: 'flex-start' } }
                  >
                    Create new room
                  </Button>
                </Stack>
              </Paper>
            ) ) }
          </Stack>
        ) }
      </Stack>
    </Box>
  )
}
